import { memo, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useOnKeydown from '../../hooks/useOnKeydown';
import { CheckmarkIcon, CloseIcon } from './icons/Icons';

const InputField = ({
	fieldName = '', label = '', labelClasses = 'text-[0.875rem]', type = 'text', initialValue = '', placeholder = '', handleChange = null, classes = '', showLabel = false, disabled = false, icon = null, useDebounce = false, autoFocus = false, clearValue = '', handleBlur = null, showCheckmark = false, afterLabelComponent = null, backgroundColor = 'bg-oly-gray-lightestAlpha45', register, error = false,
}) => {
	const [inputValue, setInputValue] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	// const [isTyping, setIsTyping] = useState(false);

	useEffect(() => {
		setInputValue(initialValue);
	}, [initialValue]);

	const debouncedHandleChange = useDebouncedCallback(
		(value) => {
			// setIsTyping(false);
			handleChange(value);
		},
		350,
	);

	const onChange = (value) => {
		setInputValue(value);
		if (handleChange) {
			if (useDebounce) {
				// setIsTyping(true);
				debouncedHandleChange(value);
			} else {
				handleChange(value);
			}
		}
	};

	const onBlur = () => {
		setIsFocused(false);
		if (handleBlur) {
			handleBlur(inputValue);
		}
	};

	const onFocus = () => {
		setIsFocused(true);
	};

	const ref = useRef();
	useOnClickOutside(ref, () => onBlur());
	useOnKeydown([9, 13], () => onBlur());

	const clear = () => {
		onChange(clearValue);
		onBlur();
	};

	return (
		<div ref={ref} className={`w-full ${classes}`}>
			<span>
				<div className="flex">
					<label htmlFor={fieldName} className={`${showLabel ? `block mx-1 ${labelClasses} ${!error ? 'text-oly-gray-darkest' : 'text-oly-red-default'}` : 'sr-only'}`}>
						{label}
					</label>
					{afterLabelComponent}
				</div>
				<div className="relative">
					<input
						id={fieldName}
						type={type}
						// required
						className={`${showLabel ? 'mt-1 text-[0.875rem]' : 'text-[1.0625rem]'} block w-full py-2 pl-3 ${(inputValue && isFocused) || (icon && !isFocused) ? 'pr-10' : 'pr-3'} transition duration-150 ease-in-out font-primary ${disabled ? 'bg-gray-200' : backgroundColor} text-oly-gray-darkest placeholder-oly-gray-default focus:outline-none focus-visible:ring-oly-blue-dark focus-visible:ring-2`}
						placeholder={placeholder}
						autoComplete="off"
						autoCorrect="off"
						onFocus={() => onFocus()}
						onChange={(e) => onChange(e.target.value || '')}
						value={register ? undefined : inputValue}
						disabled={disabled}
						autoFocus={autoFocus}
						{...register}
					/>
					{!showCheckmark && inputValue && isFocused && (
						<button
							type="button"
							onClick={() => clear()}
							className="absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none text-oly-blue-dark"
							aria-labelledby="clear-and-blur-inputfield"
						>
							<CloseIcon className={`${showLabel ? 'w-5 h-5' : 'w-6 h-6'}`} />
						</button>
					)}
					{showCheckmark && isFocused && (
						<button
							type="button"
							onClick={() => onBlur()}
							className="absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none text-oly-blue-dark"
							aria-labelledby="clear-and-blur-inputfield"
						>
							<CheckmarkIcon className={`${showLabel ? 'w-5 h-5' : 'w-6 h-6'}`} />
						</button>
					)}
					{icon && !isFocused && (
						<div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-oly-blue-dark">
							{icon}
						</div>
					)}
				</div>
			</span>
		</div>
	);
};

export default memo(InputField);
